import {
    BuildingOffice2Icon,
    ChatBubbleLeftRightIcon,
    CursorArrowRaysIcon,
    EnvelopeIcon,
    EnvelopeOpenIcon,
    PaperClipIcon,
} from "@heroicons/react/24/outline";
import { ago, SentMessageType } from "@sp-crm/core";
import { ClientPersonaLink } from "components/clients/list-client/client-persona-link";
import { AdvancedSearchEntityType } from "generated/graphql";
import React from "react";
import { Link } from "react-router-dom";
import {
    BouncedStatusMessage,
    DeliveredStatusMessage,
    NotDeliveredStatusMessage,
    ReceivedStatusMessage,
    SendingStatusMessage,
    SentStatusMessage,
} from "./status-message";
import { UnifiedMessage } from "./unified-message";

interface MessageStatusProps {
    message: UnifiedMessage;
}

const MessageStatus: React.FC<MessageStatusProps> = ({ message }) => {
    if (message.type === "received") {
        return <ReceivedStatusMessage mode="icon" />;
    }

    if (message.type === "sms") {
        return null;
    }

    const { status, latestEventStatus, messageType } = message;

    if (status === "warning") {
        return <BouncedStatusMessage mode="icon" />;
    }
    if (status === "error") {
        return <NotDeliveredStatusMessage mode="icon" />;
    }
    if (status === "success") {
        return <DeliveredStatusMessage mode="icon" />;
    }
    if (
        latestEventStatus === "fax-send-failure" ||
        latestEventStatus === "fax-delivery-failure"
    ) {
        return <NotDeliveredStatusMessage mode="icon" />;
    }
    if (latestEventStatus === "fax-delivery-success") {
        return <DeliveredStatusMessage mode="icon" />;
    }
    if (!latestEventStatus && messageType === "ClientIntakeFax") {
        return <SendingStatusMessage mode="icon" />;
    }
    if (latestEventStatus === "email-send-success") {
        return <SentStatusMessage mode="icon" />;
    }

    return null;
};

const messageTypeLabels: Record<SentMessageType, string> = {
    ClientIntake: "Client Referral",
    ClientIntakeFax: "Client Referral (fax)",
    CommunityComparison: "Community Comparison",
    Email: "",
    Invoice: "Invoice",
    DisclosureRequest: "Disclosure Request",
    DisclosureReceipt: "Disclosure Receipt",
    HostedFormRequest: "Share Input Form",
    TestOnlyMessage: "",
    SignatureCompletion: "Signature Completion",
    SignatureRequest: "Signature Request",
    BulkMessage: "Outreach",
    HostedFormReferralLink: "Share Input Form w/ Referral",
    AvailabilityRequest: "Availability Request",
    AvailabilityUpdateRequest: "Availability Update Request",
    PricingUpdateRequest: "Pricing Update Request",
    PricingRequest: "Pricing Request",
};

interface MessageRowProps {
    message: UnifiedMessage;
    onViewDetail: () => void;
    perspective: AdvancedSearchEntityType;
}

export const MessageRow: React.FC<MessageRowProps> = ({
    message,
    onViewDetail,
    perspective,
}) => {
    const messageTypeLabel =
        message.messageType && messageTypeLabels[message.messageType]
            ? messageTypeLabels[message.messageType]
            : "";

    const Icon = message.type === "sms" ? ChatBubbleLeftRightIcon : EnvelopeIcon;

    const participantLine =
        message.type === "received"
            ? `From: ${message.fromDisplay}`
            : `To: ${message.toDisplay}`;

    const showStats = message.type === "sent";

    const client =
        perspective === AdvancedSearchEntityType.Community
            ? message.intakeContext?.client
            : null;
    const community =
        perspective === AdvancedSearchEntityType.Client
            ? message.intakeContext?.community
            : null;
    const showBottomRow = showStats || !!client;

    return (
        <li className="mb-2">
            <div
                className="bg-white rounded-lg shadow px-4 py-3 cursor-pointer transition-colors hover:bg-brand-50"
                onClick={onViewDetail}>
                <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                        <Icon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </div>

                    <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium text-gray-900 truncate">
                                        {message.subject}
                                    </span>
                                    {messageTypeLabel ? (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-xs text-gray-500 flex-shrink-0">
                                            {messageTypeLabel}
                                        </span>
                                    ) : null}
                                </div>
                                {community ? (
                                    <Link
                                        to={community.appLink}
                                        className="inline-flex items-center gap-1 text-sm text-brand-600 hover:text-brand-800 hover:underline mt-0.5"
                                        onClick={e => e.stopPropagation()}>
                                        <BuildingOffice2Icon
                                            className="h-3.5 w-3.5"
                                            aria-hidden="true"
                                        />
                                        {community.name ?? "(no name)"}
                                    </Link>
                                ) : null}
                                <div className="text-sm text-gray-500 truncate mt-0.5">
                                    {participantLine}
                                </div>
                            </div>

                            <div className="flex items-center gap-2 flex-shrink-0">
                                <MessageStatus message={message} />
                                <span className="text-sm text-gray-400 whitespace-nowrap">
                                    {ago(message.timestamp, "")}
                                </span>
                            </div>
                        </div>

                        {showBottomRow ? (
                            <div className="flex justify-between items-center mt-2 gap-2">
                                {client ? (
                                    <ClientPersonaLink size="small" client={client} />
                                ) : (
                                    <div />
                                )}
                                {showStats ? (
                                    <div className="flex gap-2">
                                        {message.attachmentCount > 0 ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 text-xs text-gray-500">
                                                <PaperClipIcon className="h-4 w-4" />
                                                {message.attachmentCount}
                                            </span>
                                        ) : null}
                                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 text-xs text-gray-500">
                                            <EnvelopeOpenIcon className="h-4 w-4" />
                                            {message.openedCount}
                                        </span>
                                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 text-xs text-gray-500">
                                            <CursorArrowRaysIcon className="h-4 w-4" />
                                            {message.clickCount}
                                        </span>
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </li>
    );
};
